<template lang="pug">
  y-dialog(fill-height fluid v-model="showModal" scrollable transition="fade-transition" persistent max-width="600px")
    v-card(flat)
      v-card-title.px-5.basic200.pb-0
        v-row(no-gutters)
          v-col
            h6.basic800--text {{ $t('update_stripe') }}
          v-col.shrink
            v-btn.mr-n2(text icon @click="showModal = !showModal" height)
              v-icon(color="primary" :size="24") mdi-close

      v-card-text.pa-0(flat)
        v-form(ref="form")
          v-layout.my-6(v-if="isFetching")
            v-row.fill-height.ma-0(align='center' justify='center')
              v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
          v-card.pa-4(v-else-if="!isFetching")
            p.caption 1. {{ $t('step_1.go_to_your') }}&nbsp;
              a(href="https://dashboard.stripe.com/webhooks/" target="_blank") {{ $t('step_1.stripe_api_settings') }}
              |  {{ $t('step_1.get_following_keys') }}

            .public-key
              p.mb-3.label.basic600--text.text-uppercase.mt-2 {{ $t('public_key.label') }}
              v-text-field.p1(solo flat background-color="basic200"    height="40" v-model='publicKey' :label="$t('public_key.label')" :placeholder="$t('public_key.placeholder')" outlined dense persistent-hint :rules="rules.not_empty")

            .secret-key
              p.mb-3.label.basic600--text.text-uppercase.mt-2 {{ $t('secret_key.label') }}
              v-text-field.p1(solo flat background-color="basic200"    height="40" v-model='secretKey' :label="$t('secret_key.label')" :placeholder="$t('secret_key.placeholder')" outlined dense persistent-hint :rules="rules.valid_key")

            p.caption 2. {{ $t('step_2.add_following_webhook') }}
              br
              b {{ webhookURL }}
              br
              |  {{ $t('step_2.to_your') }}&nbsp;
              a(href="https://dashboard.stripe.com/webhooks/" target="_blank") {{ $t('stripe_webhook_settings') }}
              | .
              br
              | {{ $t('step_2.select') }}&nbsp;
              b payment_intent.succeeded&nbsp;
              | {{ $t('step_2.and') }}and&nbsp;
              b payment_intent.payment_failed
              |  {{ $t('step_2.fill_created_webhook') }}

            .webhook-secret
              p.mb-3.label.basic600--text.text-uppercase.mt-2 {{ $t('webhook_secret.label') }}
              v-text-field.p1(solo flat background-color="basic200"    height="40" v-model='webhookSecret' :label="$t('webhook_secret.label')" :placeholder="$t('webhook_secret.placeholder')" outlined dense persistent-hint :rules="rules.valid_key")
      v-card-actions(flat)
        v-spacer
        v-btn.button-Aa-small(color='primary400', text @click='showModal = false')
          | {{ $t('Cancel') }}
        v-btn.button-AA-medium.mr-2.white--text(color='info500' depressed height=32 :loading="isLoading" @click='clickSave')
          | {{ $t('update') }}

</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'UpdateBillPlz',
  props: ['open', 'company'],
  data: () => ({
    fullscreen: true,
    isFetching: true,
    publicKey: null,
    secretKey: null,
    webhookSecret: null,
    accountEnabled: true,
    gatewayId: null,
    isLoading: false,
    rules: {
      not_empty: [val => (val || '').length > 0 || 'This field is required'],
      valid_key: [
        val => (val || '').length > 0 || 'This field is required',
      ]
    }
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    showModal: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    webhookURL () {
      return `${process.env.VUE_APP_BASE_URL}/payment-callback/${this.company.slug}/stripe/`
    }
  },
  mounted () {
    this.fullscreen = window.innerWidth < 600
  },
  created () {
    this.fetchSettings()
  },
  methods: {
     ...mapActions([
      'showMessage'
    ]),
    fetchSettings () {
      this.$axios.get(`/stripe_creds/?company=${this.company.id}`).then((response) => {
        if (response.data.results.length != 0) {
          const gateway = response.data.results[0]
          this.gatewayId = gateway.id
          this.publicKey = gateway.public_key
          this.secretKey = gateway.secret_key
          this.webhookSecret = gateway.webhook_secret
          this.accountEnabled = gateway.enabled
        }
        this.isFetching = false
      })
      .catch(() => {})
    },
    clickSave() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      var params = new FormData();
      params.set('company', this.company.id)
      params.set('public_key', this.publicKey)
      params.set('secret_key', this.secretKey)
      params.set('webhook_secret', this.webhookSecret)
      params.set('enabled', this.accountEnabled)
      if (this.gatewayId) {
        this.axios.put(`/stripe_creds/${this.gatewayId}/`, params)
        .then(resp => {
          console.log(resp)
          this.showModal = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
        return
      }
      this.axios.post(`/stripe_creds/`, params)
        .then(resp => {
          this.$emit('activated', resp.data)
          this.showModal = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>

<style scoped>
.background-inventory {
  border-style: solid;
  border-width: 1px;
  border-color: grey;
  border-radius: 2px;
}
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.v-card--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
</style>

<i18n>
{
  "en": {
    "update_stripe": "Update Stripe",
    "public_key": {
      "label": "Publishable key",
      "placeholder": "Write your Stripe publishable key here"
    },
    "secret_key": {
      "label": "Secret key",
      "placeholder": "Write your Stripe secret key here"
    },
    "webhook_secret": {
      "label": "Webhook secret",
      "placeholder": "Write your Stripe webhook secret here"
    },
    "step_1": {
      "go_to_your": "Go to your",
      "stripe_api_settings": "Stripe API settings",
      "get_following_keys": "to get the following keys."
    },
    "step_2": {
      "add_following_webhook": "You must also add the following webhook endpoint",
      "to_your": "to your",
      "stripe_webhook_settings": "Stripe webhooks settings",
      "select": "Select",
      "and": "and",
      "fill_created_webhook": "as listening events. Fill in the created webhook's secret below."
    }
  }
}
</i18n>